import { useCallback, useEffect, useState } from "react";
import { FEATURE_FLAG_DEFINITION_LIST } from "@salestrak/feature-flags";
import {
  AdminFeatureFlagsDocument,
  AdminIntegrationProvidersDocument,
  AdminUserWorkspacesDocument,
  AdminWorkspacesDocument,
} from "#app/lib/data";
import { useAdminFetch } from "./admin-shared";

export type FlagScopeType = "user" | "workspace";

export type DbFlag = {
  key: string;
  name: string;
  description: string | null;
  enabled: boolean;
  scope: FlagScopeType;
  rolloutPercentage: number;
  // Whether a row exists in the DB yet. A catalog-only flag is `false` until a
  // toggle/override lazy-creates it. Drives whether a patch must send `scope`
  // (to seed the catalog scope on create) vs. omit it (to avoid clobbering a
  // persisted flag's scope on a toggle-only patch).
  persisted: boolean;
};

export type Superuser = {
  userId: string;
  name: string;
  email: string;
};

export type WorkspaceRef = {
  id: string;
  name: string;
  urlPath: string;
};

export type AdminWorkspace = WorkspaceRef & {
  memberCount: number;
  createdAt: string;
};

export type DbProvider = {
  slug: string;
  displayName: string;
  kind: string;
  featured: boolean;
  connectionCount: number;
};

/**
 * Centralized admin data: feature flags, superusers (+ their workspace
 * memberships), and all workspaces. Screens consume slices and call the
 * matching refetch after a mutation; the console derives sidebar counts.
 */
export function useAdminData() {
  const { flagsGql, coreGql, loginFetch } = useAdminFetch();

  const [flags, setFlags] = useState<DbFlag[]>([]);
  const [flagsLoading, setFlagsLoading] = useState(true);

  const [superusers, setSuperusers] = useState<Superuser[]>([]);
  const [superusersLoading, setSuperusersLoading] = useState(true);
  // userId -> their active workspaces
  const [memberships, setMemberships] = useState<Record<string, WorkspaceRef[]>>({});

  const [workspaces, setWorkspaces] = useState<AdminWorkspace[]>([]);
  const [workspacesLoading, setWorkspacesLoading] = useState(true);

  const [providers, setProviders] = useState<DbProvider[]>([]);
  const [providersLoading, setProvidersLoading] = useState(true);

  const refetchFlags = useCallback(async () => {
    setFlagsLoading(true);
    try {
      const data = await flagsGql(AdminFeatureFlagsDocument, {});
      const rows: DbFlag[] = (data.featureFlags ?? []).map((f) => ({
        key: f.key,
        name: f.name,
        description: f.description ?? null,
        enabled: f.enabled,
        scope: f.scope,
        rolloutPercentage: f.rolloutPercentage,
        persisted: true,
      }));
      const byKey = new Map(rows.map((r) => [r.key, r]));
      // The code catalog is the source of truth for which flags exist. A DB row
      // is lazy-created only when a flag is toggled/overridden, so merge: each
      // catalog flag shows its persisted row if present, else a default
      // (disabled, 100% rollout, catalog scope) the admin can edit to create it.
      const merged: DbFlag[] = FEATURE_FLAG_DEFINITION_LIST.map(
        (def) =>
          byKey.get(def.key) ?? {
            key: def.key,
            name: def.title,
            description: def.description,
            enabled: false,
            scope: def.scope,
            // No row yet → the flag reaches no one, so show 0% (matches the
            // server's lazy-create default; no jump when it's first persisted).
            rolloutPercentage: 0,
            persisted: false,
          },
      );
      // Keep any persisted rows whose key is no longer in the catalog (renamed /
      // removed) visible so they can still be deleted.
      const catalogKeys = new Set<string>(
        FEATURE_FLAG_DEFINITION_LIST.map((d) => d.key),
      );
      for (const row of rows) {
        if (!catalogKeys.has(row.key)) merged.push(row);
      }
      setFlags(merged);
    } catch {
      // silent — shown via toasts on mutations
    } finally {
      setFlagsLoading(false);
    }
  }, [flagsGql]);

  const refetchWorkspaces = useCallback(async () => {
    setWorkspacesLoading(true);
    try {
      const data = await coreGql(AdminWorkspacesDocument, {});
      setWorkspaces(
        (data.adminWorkspaces ?? []).map((w) => ({
          id: w.id,
          name: w.name,
          urlPath: w.urlPath,
          memberCount: w.memberCount,
          createdAt: w.createdAt,
        })),
      );
    } catch {
      // silent
    } finally {
      setWorkspacesLoading(false);
    }
  }, [coreGql]);

  const refetchSuperusers = useCallback(async () => {
    setSuperusersLoading(true);
    try {
      const res = await loginFetch("/oAuth/superusers");
      const body = await res.json();
      const users: Superuser[] = body.users ?? [];
      setSuperusers(users);

      if (users.length) {
        try {
          const data = await coreGql(AdminUserWorkspacesDocument, {
            userIds: users.map((u) => u.userId),
          });
          const map: Record<string, WorkspaceRef[]> = {};
          for (const row of data.adminUserWorkspaces ?? []) {
            map[row.userId] = row.workspaces.map((w) => ({
              id: w.id,
              name: w.name,
              urlPath: w.urlPath,
            }));
          }
          setMemberships(map);
        } catch {
          setMemberships({});
        }
      } else {
        setMemberships({});
      }
    } catch {
      // silent
    } finally {
      setSuperusersLoading(false);
    }
  }, [loginFetch, coreGql]);

  const refetchProviders = useCallback(async () => {
    setProvidersLoading(true);
    try {
      const data = await coreGql(AdminIntegrationProvidersDocument, {});
      setProviders(
        (data.adminIntegrationProviders ?? []).map((p) => ({
          slug: p.slug,
          displayName: p.displayName,
          kind: p.kind,
          featured: p.featured,
          connectionCount: p.connectionCount,
        })),
      );
    } catch {
      // silent — surfaced via toasts on mutations
    } finally {
      setProvidersLoading(false);
    }
  }, [coreGql]);

  useEffect(() => {
    refetchFlags();
    refetchSuperusers();
    refetchWorkspaces();
    refetchProviders();
  }, [refetchFlags, refetchSuperusers, refetchWorkspaces, refetchProviders]);

  return {
    flags,
    flagsLoading,
    refetchFlags,
    superusers,
    superusersLoading,
    memberships,
    refetchSuperusers,
    workspaces,
    workspacesLoading,
    refetchWorkspaces,
    providers,
    providersLoading,
    refetchProviders,
  };
}

export type AdminData = ReturnType<typeof useAdminData>;
